/**
 * The client's understanding of a journal entry's shape, for the offline echo.
 *
 * The server parses frontmatter on write and derives title, date, and tags for
 * the index; when a write is only queued, the echo has to produce the same row
 * from what the client sent. This mirrors `files.py create_file`,
 * `journal_index_service._extract_date`, `ContentBodyService.extract_tags`,
 * `journal_entry_repository._frontmatter_sync`, `journal.build_excerpt`, and
 * the index query's ordering. It never re-serialises a frontmatter map: the
 * client only ever writes a one- or two-key block (`titleBlock`), so a parsed
 * map is never rendered back.
 */

import { resourceKey } from '@shared/offline';

import { PY_SPACE } from './journalDefaultsApi';
import { todayKey } from '../pages/journalPathUtils';

import type { JournalIndexEntry } from './journalIndexApi';

const JOURNAL_BASE = '/api/journal';
const INDEX_URL = `${JOURNAL_BASE}/entries/index`;
const ENTRY_URL = /^\/api\/journal\/files\/by-id\/([^/]+)(?:\/frontmatter)?$/;

/**
 * The url of an entry's body — the one the entry read policy caches and every
 * body write targets. Defined here, away from the api module, so the write
 * policies and conflict handlers can name it without importing the api.
 */
export const entryBodyUrl = (entryId: string): string => `${JOURNAL_BASE}/files/by-id/${entryId}`;

/** The cache key of the whole-archive index. */
export const INDEX_KEY = resourceKey(INDEX_URL);

/** The cache key the read path uses for an entry body. */
export const entryKey = (entryId: string): string => resourceKey(entryBodyUrl(entryId));

/** The entity key every journal write and read policy uses for an entry. */
export const journalEntity = (entryId: string): string => `journal:${entryId}`;

/** The entry id in a by-id url (body or frontmatter form); empty for any other url. */
export function idFromUrl(url: string): string {
  return ENTRY_URL.exec(url.split(/[?#]/)[0]!)?.[1] ?? '';
}

const BLOCK = /^---\n([\s\S]*?)\n---\n?/;
// Python's `.strip()` and `\s`, not JS's: see PY_SPACE in journalDefaultsApi.
const PY_SPACE_EDGES = new RegExp(`^[${PY_SPACE}]+|[${PY_SPACE}]+$`, 'g');
const PY_SPACE_RUN = new RegExp(`[${PY_SPACE}]+`, 'g');
// `_HASHTAG_RE` from content_body_service.py. Python's `\w` on a str pattern
// is Unicode (letters, digits, underscore), so `#café` is a tag there too.
const HASHTAG = /#([\p{L}\p{N}_][\p{L}\p{N}_-]*)/gu;

/**
 * Strip both edges the way python-frontmatter strips `post.content` on every
 * write: Python's `.strip()`, not JS's `.trim()`. What the editor holds and
 * what the server stores differ by exactly this, so a comparison across the
 * two must apply it to both sides.
 */
export const trimEdges = (text: string): string => text.replace(PY_SPACE_EDGES, '');

/**
 * Split a leading `---` block from the body; `block` is null when there is
 * none. The body is stripped at both ends the way python-frontmatter strips
 * `post.content`, block or no block, so the client's body is byte-for-byte
 * the server's `content_body`.
 */
export function splitFrontmatterBlock(content: string): { block: string | null; body: string } {
  const match = BLOCK.exec(content);
  const rest = match ? content.slice(match[0].length) : content;
  return { block: match ? match[1]! : null, body: trimEdges(rest) };
}

const unquote = (v: string): string =>
  v
    .replace(/^"(.*)"$/s, '$1')
    .replace(/^'(.*)'$/s, '$1')
    .replace(/\\"/g, '"');

/** Parse `key: value` lines: quoted or bare scalars, flat `[a, b]` lists; anything else stays a raw string. */
export function parseSimpleFrontmatter(block: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const line of block.split('\n')) {
    const colon = line.indexOf(':');
    if (colon === -1 || /^\s/.test(line)) continue;
    const key = line.slice(0, colon).trim();
    const raw = line.slice(colon + 1).trim();
    out[key] = /^\[.*\]$/.test(raw)
      ? raw
          .slice(1, -1)
          .split(',')
          .map(v => unquote(v.trim()))
          .filter(Boolean)
      : unquote(raw);
  }
  return out;
}

/** A frontmatter block carrying only a title and, when given, tags; the one block the client writes. */
export function titleBlock(title: string, tags: string[]): string {
  const lines = [`title: ${JSON.stringify(title)}`];
  if (tags.length > 0) lines.push(`tags: [${tags.map(t => JSON.stringify(t)).join(', ')}]`);
  return `---\n${lines.join('\n')}\n---\n\n`;
}

/** `PurePosixPath(path).stem`: the last segment without its final suffix. */
function pathStem(path: string): string {
  const name = path.split('/').pop() ?? path;
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

/** Title the way `create_file` derives it: the frontmatter title, else the path stem unchanged. */
function deriveTitle(fm: Record<string, unknown>, path: string): string {
  if (typeof fm.title === 'string' && fm.title) return fm.title;
  return pathStem(path);
}

const ISO_DATE = /\d{4}-\d{2}-\d{2}/;

/**
 * Date the way `_extract_date` finds it: `created`, `date`, `creationDate`,
 * then the path — or null when the entry carries no date anywhere, which is
 * what the server's own `_extract_date` returns rather than inventing one.
 *
 * Exported because the editor has to reach the same answer the index row
 * holds. Two thirds of the archive is filed as `YYYY/Month/Title.md` with no
 * date in the path at all, so reading the path alone leaves the editor with
 * nothing to show while the sidebar row beside it names the day perfectly well.
 *
 * A frontmatter value may be a full ISO timestamp (`creationDate` on Day One
 * exports), so only the leading date is taken.
 */
export function deriveDateOrNull(
  fm: Record<string, unknown> | null | undefined,
  path: string
): string | null {
  for (const key of ['created', 'date', 'creationDate']) {
    const raw = fm?.[key];
    const found = typeof raw === 'string' ? /^\d{4}-\d{2}-\d{2}/.exec(raw)?.[0] : undefined;
    if (found) return found;
  }
  return ISO_DATE.exec(path)?.[0] ?? null;
}

/**
 * `deriveDateOrNull`, defaulting to today the way `create_file` does when an
 * entry carries no date at all.
 */
function deriveDate(fm: Record<string, unknown>, path: string): string {
  return deriveDateOrNull(fm, path) ?? todayKey();
}

/** `str(t).lower().strip()` over a frontmatter tag list, as both server derivations apply it. */
const normaliseTags = (raw: unknown[]): string[] => raw.map(t => String(t).toLowerCase().trim());

/** Tags the way a create extracts them: frontmatter tags plus inline hashtags outside headings, lowercased, sorted. */
export function extractTags(fm: Record<string, unknown>, body: string): string[] {
  const tags = new Set<string>();
  const raw = fm.tags;
  if (Array.isArray(raw)) normaliseTags(raw).forEach(t => tags.add(t));
  else if (typeof raw === 'string') raw.split(',').forEach(t => tags.add(t.toLowerCase().trim()));
  for (const line of body.split('\n')) {
    if (line.trimStart().startsWith('# ')) continue;
    for (const m of line.matchAll(HASHTAG)) tags.add(m[1]!.toLowerCase());
  }
  tags.delete('');
  return [...tags].sort();
}

/**
 * A frontmatter map laid over an index row the way a PATCH lands on the
 * server (`_frontmatter_sync`): a non-empty title replaces the title, a tag
 * list replaces the tags, lowercased and stripped, and anything else leaves
 * the row's own columns alone. No hashtags: only a create extracts those.
 */
export function overlayFrontmatter(
  row: JournalIndexEntry,
  fm: Record<string, unknown>
): JournalIndexEntry {
  return {
    ...row,
    title: fm.title ? String(fm.title) : row.title,
    tags: Array.isArray(fm.tags) ? normaliseTags(fm.tags) : row.tags,
  };
}

/**
 * The index in the order the server lists it: newest date first, and within
 * a date in the order the rows were given (the server's `created_at asc`),
 * which is why callers append a new row rather than prepend it.
 */
export function sortIndexRows(rows: JournalIndexEntry[]): JournalIndexEntry[] {
  return [...rows].sort((a, b) => b.entry_date.localeCompare(a.entry_date));
}

// The server's `build_excerpt` patterns, one for one.
const EXCERPT_LIMIT = 200;
const FRONTMATTER_BLOCK = /^---\s*\n[\s\S]*?\n---[ \t]*(?:\n|$)/;
const MD_IMAGE = /!\[[^\]]*\]\([^)]*\)/g;
const MD_LINK = /\[([^\]]*)\]\([^)]*\)/g;
const MD_LINE_PREFIX = /^[ \t]*(?:#{1,6}|>|[-*+]|\d+\.)[ \t]+/gm;
const MD_EMPHASIS = /[*_`~]+/g;

/**
 * The sidebar's one-line excerpt, flattened the way the server's
 * `build_excerpt` flattens it. Python slices and measures code points where
 * JS counts UTF-16 units, so the clip works on `Array.from(text)`: an emoji
 * is one character on both sides.
 */
export function buildExcerpt(body: string): string {
  if (!body) return '';
  const text = trimEdges(
    body
      .replace(FRONTMATTER_BLOCK, '')
      .replace(MD_IMAGE, '')
      .replace(MD_LINK, '$1')
      .replace(MD_LINE_PREFIX, '')
      .replace(MD_EMPHASIS, '')
      .replace(PY_SPACE_RUN, ' ')
  );
  const chars = Array.from(text);
  if (chars.length <= EXCERPT_LIMIT) return text;
  // Only spaces remain after the collapse, so `rstrip()` is a space trim.
  let clipped = chars.slice(0, EXCERPT_LIMIT).join('').replace(/ +$/, '');
  const lastSpace = clipped.lastIndexOf(' ');
  const onWord = lastSpace === -1 ? clipped : clipped.slice(0, lastSpace);
  // Only honour the word boundary when it is not far back from the limit.
  if (Array.from(onWord).length >= EXCERPT_LIMIT * 0.6) clipped = onWord;
  return `${clipped.replace(/ +$/, '')}…`;
}

/** An entry whose frontmatter is already parsed, as a create reply or a cached entry holds it. */
export interface ParsedEntry {
  id: string;
  path: string;
  frontmatter: Record<string, unknown>;
  body: string;
}

/** The sidebar row the server's index builds for an already-parsed entry. */
export function indexRowFromEntry({ id, path, frontmatter, body }: ParsedEntry): JournalIndexEntry {
  return {
    id,
    entry_date: deriveDate(frontmatter, path),
    title: deriveTitle(frontmatter, path),
    file_path: path,
    tags: extractTags(frontmatter, body),
    excerpt: buildExcerpt(body),
  };
}

/** The sidebar row a create will produce, built the way the server's index builds it. */
export function indexRowFor(req: { id: string; path: string; content: string }): JournalIndexEntry {
  const { block, body } = splitFrontmatterBlock(req.content);
  const frontmatter = block ? parseSimpleFrontmatter(block) : {};
  return indexRowFromEntry({ id: req.id, path: req.path, frontmatter, body });
}
